import { useState, useEffect, useMemo } from "react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { StatCard } from "../components/common/StatCard";
import { DataTable } from "../components/common/DataTable";
import { FilterBar } from "../components/common/FilterBar";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { useFiltering } from "../hooks/useFiltering";
import { useNotification } from "../hooks/useNotification";
import { useScopeFilter } from "../hooks/useScopeFilter";
import { usersAPI, schoolsAPI, campusAPI, rolesAPI } from "../api";
import { User, Campus } from "../types";
import type { Role } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatDate } from "../utils/formatters";
import { getSchoolName, handleSchoolId } from "../utils/helpers";
import { ResetUserPasswordModal } from "../components/ResetUserPasswordModal";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { 
  Users, 
  UserCheck, 
  UserX, 
  GraduationCap,
  Plus,
  Mail,
  Lock,
  User as UserIcon,
  Building,
  Loader2,
  Eye,
  EyeOff,
  Key
} from "lucide-react";

export function UsersPageEnhanced() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [selectedCampus, setSelectedCampus] = useState<string>("");
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const { showNotification } = useNotification();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    password_confirmed: "",
    first_name: "",
    last_name: "",
    role: "user",
    is_active: true,
    is_email_verified: false,
  });

  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useDataFetching(QUERY_KEYS.USERS, {
    queryFn: usersAPI.getAllUsers,
  });

  const { data: schoolsData } = useDataFetching(QUERY_KEYS.SCHOOLS, {
    queryFn: schoolsAPI.getAllSchools,
  });

  const { data: allCampusesData } = useDataFetching(QUERY_KEYS.CAMPUSES, {
    queryFn: campusAPI.getAllCampuses,
  });

  const { data: rolesData } = useDataFetching(QUERY_KEYS.ROLES, {
    queryFn: rolesAPI.getRoles,
  });

  const users = useMemo(
    () => (Array.isArray(usersData) ? usersData : []),
    [usersData]
  );
  const schools = Array.isArray(schoolsData) ? schoolsData : [];
  const roles = rolesData?.roles || [];
  const allCampuses = useMemo(() => {
    return Array.isArray(allCampusesData) ? allCampusesData : 
           allCampusesData?.data ? allCampusesData.data : [];
  }, [allCampusesData]);

  // Filter campuses when school changes
  useEffect(() => {
    if (selectedSchool && allCampuses.length > 0) {
      const filteredCampuses = allCampuses.filter((campus: Campus) => 
        campus.school_id === selectedSchool
      );
      setCampuses(filteredCampuses);
      // Auto-select first campus if available
      if (filteredCampuses.length > 0) {
        setSelectedCampus(filteredCampuses[0]._id);
      } else {
        setSelectedCampus("");
      }
    } else {
      setCampuses([]);
      setSelectedCampus("");
    }
  }, [selectedSchool, allCampuses]);

  const { filterByScope } = useScopeFilter();
  const scopedUsers = useMemo(
    () => filterByScope(users),
    [users, filterByScope]
  );

  const {
    searchTerm,
    setSearchTerm,
    filteredData: filteredUsers,
  } = useFiltering({
    data: scopedUsers,
    searchFields: ['first_name', 'last_name', 'email'],
  });

  const activeUsers = filteredUsers.filter((user: User) => user.is_active).length;
  const inactiveUsers = filteredUsers.filter((user: User) => !user.is_active).length;
  const verifiedUsers = filteredUsers.filter((user: User) => user.is_email_verified).length;

  const handleCreateUser = async () => {
    setFormErrors({});
    setPasswordErrors([]);
    // Validation
    if (!formData.email || !formData.password) {
      const errs: Record<string, string> = {};
      if (!formData.email) errs.email = "Email is required";
      if (!formData.password) errs.password = "Password is required";
      setFormErrors(errs);
      showNotification("Email and password are required", "error");
      return;
    }

    if (formData.password !== formData.password_confirmed) {
      setFormErrors(prev => ({ ...prev, password_confirmed: "Passwords do not match" }));
      showNotification("Passwords do not match", "error");
      return;
    }

    if (formData.password.length < 6) {
      setFormErrors(prev => ({ ...prev, password: "Password must be at least 6 characters" }));
      showNotification("Password must be at least 6 characters", "error");
      return;
    }

    if (!selectedSchool) {
      setFormErrors(prev => ({ ...prev, school_id: "Please select a school" }));
      showNotification("Please select a school", "error");
      return;
    }

    try {
      setIsCreating(true);
      
      const userData = {
        ...formData,
        school_id: selectedSchool,
        campus_id: selectedCampus || undefined,
      };

      console.log('Creating user with data:', userData);
      
      await usersAPI.createUser(userData);
      
      showNotification("User created successfully", "success");
      
      // Reset form
      setFormData({
        email: "",
        password: "",
        password_confirmed: "",
        first_name: "",
        last_name: "",
        role: "user",
        is_active: true,
        is_email_verified: false,
      });
      setSelectedSchool("");
      setSelectedCampus("");
      setIsCreateDialogOpen(false);
      setPasswordErrors([]);
      
      // Refresh users list
      refetchUsers();
      
    } catch (error: unknown) {
      console.error('Error creating user:', error);
      const axiosErr = error as Error & { response?: { data?: { errors?: Array<{ path?: string; msg?: string } | string>; message?: string } } };
      const serverErrors = axiosErr?.response?.data?.errors;
      const serverMsg = axiosErr?.response?.data?.message;
      if (Array.isArray(serverErrors) && serverErrors.length > 0) {
        if (typeof serverErrors[0] === 'string') {
          setPasswordErrors(serverErrors as string[]);
          showNotification(serverMsg || 'Password validation failed', 'error');
        } else {
          const fieldErrors: Record<string, string> = {};
          serverErrors.forEach((err: { path?: string; msg?: string } | string) => {
            if (typeof err === 'object' && err?.path && err?.msg) fieldErrors[err.path] = err.msg;
          });
          // Enhance email error with expected domain if a school is selected
          if (fieldErrors.email && selectedSchool) {
            const schoolObj = schools.find((s) => s._id === selectedSchool);
            if (schoolObj?.email_domain) {
              fieldErrors.email = `${fieldErrors.email}. Expected domain: @${schoolObj.email_domain}`;
            }
          }
          setFormErrors(fieldErrors);
          let firstMsg = serverErrors[0]?.msg || 'Validation failed';
          if (serverErrors[0]?.path === 'email' && selectedSchool) {
            const schoolObj = schools.find((s) => s._id === selectedSchool);
            if (schoolObj?.email_domain) {
              firstMsg = `${firstMsg}. Expected domain: @${schoolObj.email_domain}`;
            }
          }
          showNotification(firstMsg, 'error');
        }
      } else {
        const errorMessage = axiosErr?.response?.data?.message || axiosErr?.message || 'Failed to create user';
        showNotification(errorMessage, 'error');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const columns = [
    {
      key: 'profile',
      header: 'User',
      render: (user: User) => (
        <div 
          className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            setSelectedUser(user);
            setIsDetailsDialogOpen(true);
          }}
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.avatar_url} alt={`${user.first_name} ${user.last_name}`} />
            <AvatarFallback>
              {user.first_name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-blue-600 hover:text-blue-800 underline">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User'}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'school_id',
      header: 'School',
      render: (user: User) => (
        <span className="text-sm">
          {getSchoolName(handleSchoolId(user.school_id || ''), schools)}
        </span>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: User) => (
        <Badge variant={user.role === 'admin' ? "destructive" : "default"}>
          {user.role || 'user'}
        </Badge>
      ),
    },
    {
      key: 'otp',
      header: 'OTP',
      render: (user: User) => (
        <span className="text-sm font-mono">
          {user.otp || '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: User) => (
        <div className="flex flex-col gap-1">
          <Badge variant={user.is_active ? "default" : "secondary"}>
            {user.is_active ? "Active" : "Inactive"}
          </Badge>
          {user.is_email_verified && (
            <Badge variant="outline" className="text-xs">
              <Mail className="h-3 w-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      render: (user: User) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(user.createdAt)}
        </span>
      ),
    },
  ];

  if (usersLoading) {
    return (
      <DashboardLayout>
        <LoadingState />
      </DashboardLayout>
    );
  }

  if (usersError) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load users" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageHeader
            title="Users"
            description="Manage all users in the system"
          />
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create User
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <StatCard
            title="Total Users"
            value={filteredUsers.length}
            description="All users"
            icon={<Users className="h-4 w-4" />}
            variant="blue"
          />
          <StatCard
            title="Active Users"
            value={activeUsers}
            description="Currently active"
            icon={<UserCheck className="h-4 w-4" />}
            variant="green"
          />
          <StatCard
            title="Inactive Users"
            value={inactiveUsers}
            description="Not active"
            icon={<UserX className="h-4 w-4" />}
            variant="amber"
          />
          <StatCard
            title="Verified Users"
            value={verifiedUsers}
            description="Email verified"
            icon={<Mail className="h-4 w-4" />}
            variant="purple"
          />
        </div>

        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search users by name or email..."
        />

        <DataTable
          columns={columns}
          data={filteredUsers}
          emptyMessage="No users found"
        />
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create a new user account with school and campus assignment
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">
                  <UserIcon className="h-4 w-4 inline mr-1" />
                  First Name
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div>
                <Label htmlFor="last_name">
                  <UserIcon className="h-4 w-4 inline mr-1" />
                  Last Name
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">
                <Mail className="h-4 w-4 inline mr-1" />
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setFormErrors(prev => ({ ...prev, email: '' })); }}
                placeholder="user@example.com"
                required
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
              )}
              {selectedSchool && (() => {
                const schoolObj = schools.find((s) => s._id === selectedSchool);
                return schoolObj?.email_domain ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Allowed domain: @{schoolObj.email_domain}
                  </p>
                ) : null;
              })()}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password">
                  <Lock className="h-4 w-4 inline mr-1" />
                  Password *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setFormErrors(prev => ({ ...prev, password: '' })); setPasswordErrors([]); }}
                    placeholder="Min. 6 characters"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {formErrors.password && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
                )}
                {passwordErrors.length > 0 && (
                  <ul className="mt-1 text-sm text-red-500 list-disc pl-5">
                    {passwordErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <Label htmlFor="password_confirmed">
                  <Lock className="h-4 w-4 inline mr-1" />
                  Confirm Password *
                </Label>
                <Input
                  id="password_confirmed"
                  type={showPassword ? "text" : "password"}
                  value={formData.password_confirmed}
                  onChange={(e) => { setFormData({ ...formData, password_confirmed: e.target.value }); setFormErrors(prev => ({ ...prev, password_confirmed: '' })); }}
                  placeholder="Confirm password"
                  required
                />
                {formErrors.password_confirmed && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.password_confirmed}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="school">
                  <GraduationCap className="h-4 w-4 inline mr-1" />
                  School *
                </Label>
                <Select value={selectedSchool} onValueChange={(val) => { setSelectedSchool(val); setFormErrors(prev => ({ ...prev, school_id: '' })); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a school" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map((school) => (
                      <SelectItem key={school._id} value={school._id}>
                        {school.display_name || school.school_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.school_id && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.school_id}</p>
                )}
              </div>
              <div>
                <Label htmlFor="campus">
                  <Building className="h-4 w-4 inline mr-1" />
                  Campus
                </Label>
                <Select value={selectedCampus} onValueChange={setSelectedCampus} disabled={!selectedSchool}>
                  <SelectTrigger>
                    <SelectValue placeholder={selectedSchool ? "Select a campus" : "Select school first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {campuses.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No campuses available
                      </div>
                    ) : (
                      campuses.map((campus) => (
                        <SelectItem key={campus._id} value={campus._id}>
                          {campus.campus_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="role">
                Role
              </Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue placeholder={roles.length === 0 ? "Loading roles..." : "Select a role"} />
                </SelectTrigger>
                <SelectContent>
                  {roles.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Loading roles...
                    </div>
                  ) : (
                    roles
                      .filter((role: Role) => role.name !== "super_admin")
                      .map((role: Role) => (
                        <SelectItem key={role._id} value={role.name}>
                          {role.display_name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Account is active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_email_verified"
                  checked={formData.is_email_verified}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_email_verified: checked })}
                />
                <Label htmlFor="is_email_verified">Email verified</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              Complete information about the selected user
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6 py-4">
              {/* Profile Section */}
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={selectedUser.avatar_url} alt={`${selectedUser.first_name} ${selectedUser.last_name}`} />
                  <AvatarFallback className="text-2xl">
                    {selectedUser.first_name?.charAt(0)?.toUpperCase() || 'U'}
                    {selectedUser.last_name?.charAt(0)?.toUpperCase() || ''}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-2xl font-semibold">
                    {`${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || 'Unknown User'}
                  </h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">User ID</Label>
                    <p className="font-mono text-sm">{selectedUser._id}</p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="text-sm">{selectedUser.email}</p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Role</Label>
                    <Badge variant={selectedUser.role === 'admin' ? "destructive" : "default"}>
                      {selectedUser.role || 'user'}
                    </Badge>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">School</Label>
                    <p className="text-sm">
                      {selectedUser.school_id ? 
                        getSchoolName(handleSchoolId(selectedUser.school_id), schools) : 
                        'Not assigned'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Campus</Label>
                    <p className="text-sm">
                      {selectedUser.campus_id ? 
                        allCampuses.find((c: Campus) => c._id === selectedUser.campus_id)?.name || 'Unknown' :
                        'Not assigned'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">OTP Code</Label>
                    <p className="font-mono text-lg font-semibold text-blue-600">
                      {selectedUser.otp || 'Not set'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <div className="flex gap-2">
                      <Badge variant={selectedUser.is_active ? "default" : "secondary"}>
                        {selectedUser.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {selectedUser.is_email_verified && (
                        <Badge variant="outline">
                          <Mail className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Profile Bio</Label>
                    <p className="text-sm">
                      {selectedUser.profile_bio || 'No bio provided'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Created At</Label>
                    <p className="text-sm">{formatDate(selectedUser.createdAt)}</p>
                  </div>
                  
                  {selectedUser.updatedAt && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Updated At</Label>
                      <p className="text-sm">{formatDate(selectedUser.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              {selectedUser.interest_ids && selectedUser.interest_ids.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Interests</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedUser.interest_ids.map((item: string | { _id: string; name: string }) => {
                      const id = typeof item === 'string' ? item : item._id;
                      const label = typeof item === 'string' ? item : item.name;
                      return (
                        <Badge key={id} variant="outline">{label}</Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsDialogOpen(false)}>
              Close
            </Button>
            {selectedUser && (
              <Button onClick={() => setIsResetPasswordModalOpen(true)}>
                <Key className="h-4 w-4 mr-2" />
                Reset Password
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResetUserPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        user={selectedUser}
      />
    </DashboardLayout>
  );
}